import { FC, useState, useRef } from 'react';
import { useLoaderData } from 'react-router-dom';
import styled from 'styled-components';

import {
  apiListPpvDescriptionByInputSearch,
  apiListPpvByPage,
} from '../components/ui_components/ApiList';

import SingleCardPpv from '../components/ppv_components/SingleCardPpv';
import SearchPpv from '../components/ppv_components/SearchPpv';
import BoxPage from '../components/ppv_components/BoxPage';
import SelectYear from '../components/ppv_components/SelectYear';

import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';
import type { PPVGeneral } from '../types';

const MainContainer = styled.div``;

const TopSize = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding: 5px;
`;

const ListPpvContainer = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const PpvGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
`;

interface PPVListResponse {
  data: PPVGeneral[];
  last_page: number;
  current_page: number;
  name?: string;
}

const ListPpv: FC = () => {
  const loaderData = useLoaderData() as PPVListResponse;
  const [responseData, setResponseData] = useState<PPVListResponse>(loaderData);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const updateListPpvWithSearchBar = async (value: string): Promise<void> => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    try {
      setIsLoading(true);
      const response = await apiListPpvDescriptionByInputSearch(value);
      if (!signal.aborted) setResponseData(response as unknown as PPVListResponse);
    } catch (error) {
      if (!signal.aborted) console.error('Error during API request:', error);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const updateDataByPage = async (page: number): Promise<void> => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    try {
      setIsLoading(true);
      const response = await apiListPpvByPage(page);
      if (!signal.aborted) setResponseData(response as unknown as PPVListResponse);
    } catch (error) {
      if (!signal.aborted) console.error('Error during API request:', error);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  return (
    <CommonWrapper className="wrapper-ListPpv">
      <MainContainer className="container">
        {isLoading && <p>Caricamento..</p>}

        <TopSize className="row TopSize">
          <SearchPpv listPpv={responseData.data} onFilterTextChange={updateListPpvWithSearchBar} />

          <BoxPage
            className="col-4"
            maxPageApi={responseData.last_page}
            currentPage={responseData.current_page}
            onPageChange={updateDataByPage}
          />

          <SelectYear />
        </TopSize>

        {!isLoading && (
          <ListPpvContainer>
            <PpvGrid>
              {responseData.data.map((ppv, index) => (
                <SingleCardPpv
                  key={'ppv-' + index + '-' + ppv.id}
                  id={'ppv-' + index + '-' + ppv.id}
                  ppv={ppv}
                  namePpv={responseData.name}
                />
              ))}
            </PpvGrid>
          </ListPpvContainer>
        )}
      </MainContainer>
    </CommonWrapper>
  );
};

export default ListPpv;

export async function loader(): Promise<PPVListResponse> {
  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/ppv/list?page=1');
  if (!response.ok) {
    throw new Error('Failed to load PPV list');
  }
  const resData = await response.json();
  return resData;
}
